import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { childDashboardService, type Child as ChildType, type ChildCourse, type ChildActivity, type ChildAchievement, type ChildLearningInsight } from '../services/childDashboardService';
import SessionHistoryList from '../components/session/SessionHistoryList';
import { getParentSessionHistory, SessionHistoryItem } from '../services/tutorSessionHistoryService';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Grid,
  Avatar,
  Chip,
  Button,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  ListItemIcon,
  Divider,
  LinearProgress,
  Paper,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tabs,
  Tab,
  Badge,
  Tooltip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  ArrowBack,
  Person,
  School,
  TrendingUp,
  Assignment,
  Quiz,
  EmojiEvents,
  Schedule,
  Visibility,
  Message,
  Warning,
  CheckCircle,
  AccessTime,
  Star,
  Book,
  Psychology,
  Science,
  Calculate,
  Language,
  History,
  Brush,
  MusicNote,
  SportsEsports,
  Timer,
  CalendarToday,
  NotificationImportant,
  ThumbUp,
  Flag,
  VideoLibrary,
  MenuBook,
  Speed,
  TrendingDown,
  Help,
  Chat,
  Phone,
  Email,
} from '@mui/icons-material';

// Types are imported from childDashboardService

const ParentChildDashboard: React.FC = () => {
  const { childId } = useParams<{ childId: string }>();
  const navigate = useNavigate();
  const [child, setChild] = useState<ChildType | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState(0);
  const [showMessageDialog, setShowMessageDialog] = useState(false);
  const [showNotesDialog, setShowNotesDialog] = useState(false);
  const [parentNotes, setParentNotes] = useState('');
  const [sessionHistoryByCourse, setSessionHistoryByCourse] = useState<Record<number, SessionHistoryItem[]>>({});
  const [sessionHistoryLoadingCourse, setSessionHistoryLoadingCourse] = useState<number | null>(null);

  useEffect(() => {
    const loadChildDashboard = async () => {
      if (!childId) {
        setError('Child ID is required');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        const dashboardData = await childDashboardService.getChildDashboard(parseInt(childId));
        setChild(dashboardData);
        setParentNotes(dashboardData.parentNotes || '');
      } catch (err: any) {
        console.error('Error loading child dashboard:', err);
        setError(err.message || 'Failed to load child dashboard data');
      } finally {
        setLoading(false);
      }
    };

    loadChildDashboard();
  }, [childId]);

  const handleBackToDashboard = () => {
    navigate('/parent-dashboard');
  };

  const handleSendMessage = () => {
    setShowMessageDialog(true);
  };

  const handleSaveNotes = () => {
    if (child) {
      // TODO: Implement API call to save parent notes
      setChild({ ...child, parentNotes });
      setShowNotesDialog(false);
    }
  };

  const getActivityIcon = (type: ChildActivity['type']) => {
    switch (type) {
      case 'lesson_completed':
        return <CheckCircle color="success" />;
      case 'quiz_passed':
        return <Quiz color="success" />;
      case 'quiz_failed':
        return <Quiz color="error" />;
      case 'assignment_submitted':
        return <Assignment color="primary" />;
      case 'badge_earned':
        return <EmojiEvents color="warning" />;
      case 'course_started':
        return <School color="info" />;
      case 'study_session':
        return <Timer color="primary" />;
      case 'help_requested':
        return <Help color="warning" />;
      default:
        return <School />;
    }
  };

  const getSubjectIcon = (subject: string) => {
    switch (subject.toLowerCase()) {
      case 'mathematics':
        return <Calculate color="primary" />;
      case 'english':
        return <Language color="secondary" />;
      case 'science':
        return <Science color="success" />;
      case 'history':
        return <History color="info" />;
      case 'art':
        return <Brush color="warning" />;
      case 'music':
        return <MusicNote color="error" />;
      default:
        return <Book />;
    }
  };

  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty) {
      case 'easy':
        return 'success';
      case 'medium':
        return 'warning';
      case 'hard':
        return 'error';
      default:
        return 'default';
    }
  };

  const getInsightIcon = (type: ChildLearningInsight['type']) => {
    switch (type) {
      case 'strength':
        return <ThumbUp color="success" />;
      case 'improvement':
        return <TrendingUp color="warning" />;
      case 'concern':
        return <Warning color="error" />;
      case 'recommendation':
        return <Star color="info" />;
      default:
        return <Star />;
    }
  };

  const formatTime = (minutes: number) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress size={60} />
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Alert severity="error">{error}</Alert>
        <Button onClick={() => navigate('/parent-dashboard')} sx={{ mt: 2 }}>
          Back to Dashboard
        </Button>
      </Container>
    );
  }

  if (!child) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Alert severity="error">Child not found.</Alert>
        <Button onClick={() => navigate('/parent-dashboard')} sx={{ mt: 2 }}>
          Back to Dashboard
        </Button>
      </Container>
    );
  }

  // Helper to format date strings
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  const formatDateTime = (dateString: string) => {
    const date = new Date(dateString);
    return `${date.toLocaleDateString()} at ${date.toLocaleTimeString()}`;
  };

  const courses = child.courses || [];
  const activities = child.activities || [];
  const achievements = child.achievements || [];
  const insights = child.insights || [];

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      {/* Header */}
      <Paper elevation={1} sx={{ bgcolor: 'white', borderBottom: 1, borderColor: 'divider' }}>
        <Container maxWidth="xl">
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <IconButton onClick={handleBackToDashboard}>
                <ArrowBack />
              </IconButton>
              <Avatar sx={{ bgcolor: 'primary.main', width: 48, height: 48 }}>
                {child.avatar}
              </Avatar>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {child.name}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    {child.age !== null ? `${child.age} years` : 'Age not specified'} • {child.grade} • {child.school}
                  </Typography>
                  <Chip 
                    label={child.status === 'active' ? 'Online' : 'Offline'} 
                    color={child.status === 'active' ? 'success' : 'default'}
                    size="small" 
                  />
                </Box>
              </Box>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Button
                variant="contained"
                startIcon={<School />}
                onClick={() => navigate(`/parent-register-child/${child?.id ?? childId}`)}
                size="small"
              >
                Find a course
              </Button>
              <Tooltip title="Send Message to Teacher">
                <IconButton onClick={handleSendMessage} color="primary">
                  <Message />
                </IconButton>
              </Tooltip>
              <Tooltip title="Schedule Parent-Teacher Conference">
                <IconButton color="info">
                  <CalendarToday />
                </IconButton>
              </Tooltip>
              <Tooltip title="View Full Report">
                <IconButton color="secondary">
                  <Visibility />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        </Container>
      </Paper>

      <Container maxWidth="xl" sx={{ py: 4 }}>
        {/* Key Metrics */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent sx={{ textAlign: 'center' }}>
                <TrendingUp sx={{ fontSize: 40, color: 'success.main', mb: 1 }} />
                <Typography variant="h4" sx={{ fontWeight: 600, color: 'success.main' }}>
                  {child.averageScore !== null ? `${Math.round(child.averageScore)}%` : 'N/A'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Average Score
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent sx={{ textAlign: 'center' }}>
                <AccessTime sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
                <Typography variant="h4" sx={{ fontWeight: 600, color: 'primary.main' }}>
                  {formatTime(child.totalStudyTime)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Total Study Time
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent sx={{ textAlign: 'center' }}>
                <EmojiEvents sx={{ fontSize: 40, color: 'warning.main', mb: 1 }} />
                <Typography variant="h4" sx={{ fontWeight: 600, color: 'warning.main' }}>
                  {child.currentStreak}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Day Streak
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent sx={{ textAlign: 'center' }}>
                <School sx={{ fontSize: 40, color: 'info.main', mb: 1 }} />
                <Typography variant="h4" sx={{ fontWeight: 600, color: 'info.main' }}>
                  {child.completedCourses}/{child.totalCourses}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Courses Completed
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Main Content Tabs */}
        <Paper sx={{ mb: 4 }}>
          <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)}>
            <Tab label="Courses & Progress" />
            <Tab label="Recent Activity" />
            <Tab label="Achievements" />
            <Tab label="Learning Insights" />
            <Tab label="Sessions" />
            <Tab label="Parent Notes" />
          </Tabs>
        </Paper>

        {/* Tab Content */}
        {activeTab === 0 && (
          <Grid container spacing={3}>
            {courses.length === 0 ? (
              <Grid item xs={12}>
                <Card>
                  <CardContent sx={{ textAlign: 'center', py: 6 }}>
                    <School sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
                    <Typography variant="h6" sx={{ mb: 1 }}>
                      No Courses Enrolled Yet
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                      Your child hasn't registered for any courses yet. Register for a course to get started!
                    </Typography>
                    <Button
                      variant="contained"
                      onClick={() => navigate(`/parent-register-child/${child.id}`)}
                    >
                      Find a course
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
            ) : (
              courses.map((course) => (
                <Grid item xs={12} md={6} key={course.id}>
                <Card 
                  sx={{ 
                    cursor: 'pointer',
                    '&:hover': {
                      boxShadow: 4,
                      transform: 'translateY(-2px)',
                      transition: 'all 0.3s ease',
                    }
                  }}
                  onClick={() => navigate(`/parent-child-dashboard/${child.id}/course/${course.id}`)}
                >
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                      {getSubjectIcon(course.subject)}
                      <Box sx={{ flexGrow: 1 }}>
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                          {course.name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {course.subject} • {course.currentTopic}
                        </Typography>
                      </Box>
                      <Box sx={{ textAlign: 'right' }}>
                        <Chip 
                          label={course.difficulty} 
                          color={getDifficultyColor(course.difficulty) as any}
                          size="small" 
                        />
                      </Box>
                    </Box>

                    <Box sx={{ mb: 2 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="body2" color="text.secondary">
                          Progress
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {Math.round(course.progress || 0)}%
                        </Typography>
                      </Box>
                      <LinearProgress 
                        variant="determinate" 
                        value={course.progress || 0} 
                        sx={{ height: 8, borderRadius: 4 }}
                      />
                    </Box>

                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Box>
                        <Typography variant="caption" color="text.secondary" display="block">
                          Avg Score: {course.averageScore !== null ? `${Math.round(course.averageScore)}%` : 'N/A'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" display="block">
                          Time: {formatTime(course.timeSpent)}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary" display="block">
                          Last accessed:
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {formatDate(course.lastAccessed)}
                        </Typography>
                      </Box>
                    </Box>

                    {course.nextDeadline && (
                      <Alert severity="info" sx={{ mt: 2 }}>
                        <Typography variant="body2">
                          Next deadline: {formatDate(course.nextDeadline)}
                        </Typography>
                      </Alert>
                    )}
                  </CardContent>
                </Card>
                </Grid>
              ))
            )}
          </Grid>
        )}

        {activeTab === 1 && (
          <Card>
            <CardContent>
              {activities.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 6 }}>
                  <Assignment sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
                  <Typography variant="h6" sx={{ mb: 1 }}>
                    No Recent Activity
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Your child hasn't completed any activities yet. Activity will appear here once they start learning.
                  </Typography>
                </Box>
              ) : (
                <List>
                  {activities.map((activity, index) => (
                  <React.Fragment key={activity.id}>
                    <ListItem sx={{ px: 0 }}>
                      <ListItemAvatar>
                        <Avatar sx={{ bgcolor: 'background.paper' }}>
                          {getActivityIcon(activity.type)}
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                              {activity.title}
                            </Typography>
                            {activity.score && (
                              <Chip 
                                label={`${activity.score}%`} 
                                size="small" 
                                color={activity.status === 'success' ? 'success' : 'error'}
                                variant="outlined"
                              />
                            )}
                          </Box>
                        }
                        secondary={
                          <Box component="span">
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1, display: 'block' }} component="span">
                              {activity.description}
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }} component="span">
                              <Typography variant="caption" color="text.secondary" component="span">
                                {activity.courseName}
                              </Typography>
                              <Typography variant="caption" color="text.secondary" component="span">
                                {formatDateTime(activity.timestamp)}
                              </Typography>
                              {activity.timeSpent && (
                                <Typography variant="caption" color="text.secondary" component="span">
                                  {formatTime(activity.timeSpent)}
                                </Typography>
                              )}
                            </Box>
                          </Box>
                        }
                      />
                    </ListItem>
                    {index < activities.length - 1 && <Divider />}
                  </React.Fragment>
                  ))}
                </List>
              )}
            </CardContent>
          </Card>
        )}

        {activeTab === 2 && (
          <Grid container spacing={3}>
            {achievements.length === 0 ? (
              <Grid item xs={12}>
                <Card>
                  <CardContent sx={{ textAlign: 'center', py: 6 }}>
                    <EmojiEvents sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
                    <Typography variant="h6" sx={{ mb: 1 }}>
                      No Achievements Yet
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Achievements will appear here as your child progresses through their courses.
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ) : (
              achievements.map((achievement) => (
                <Grid item xs={12} sm={6} md={4} key={achievement.id}>
                <Card>
                  <CardContent sx={{ textAlign: 'center' }}>
                    <Typography variant="h2" sx={{ mb: 2 }}>
                      {achievement.icon}
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                      {achievement.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      {achievement.description}
                    </Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, mb: 2 }}>
                      <Chip label={achievement.category} size="small" />
                      <Chip 
                        label={achievement.rarity} 
                        color={
                          achievement.rarity === 'legendary' ? 'warning' :
                          achievement.rarity === 'epic' ? 'secondary' :
                          achievement.rarity === 'rare' ? 'primary' : 'default'
                        }
                        size="small" 
                      />
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                      Earned {formatDate(achievement.earnedDate)}
                    </Typography>
                  </CardContent>
                </Card>
                </Grid>
              ))
            )}
          </Grid>
        )}

        {activeTab === 3 && (
          <Grid container spacing={3}>
            {insights.length === 0 ? (
              <Grid item xs={12}>
                <Card>
                  <CardContent sx={{ textAlign: 'center', py: 6 }}>
                    <Psychology sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
                    <Typography variant="h6" sx={{ mb: 1 }}>
                      No Insights Available Yet
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Learning insights will appear here as your child engages with courses and completes activities.
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ) : (
              insights.map((insight) => (
                <Grid item xs={12} key={insight.id}>
                <Card>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                      {getInsightIcon(insight.type)}
                      <Box sx={{ flexGrow: 1 }}>
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                          {insight.title}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {insight.description}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Chip 
                          label={insight.type} 
                          color={
                            insight.type === 'strength' ? 'success' :
                            insight.type === 'improvement' ? 'warning' :
                            insight.type === 'concern' ? 'error' : 'info'
                          }
                          size="small" 
                        />
                        <Chip 
                          label={`${insight.priority} priority`} 
                          variant="outlined"
                          size="small" 
                        />
                      </Box>
                    </Box>
                    {insight.actionable && (
                      <Button variant="outlined" size="small">
                        Take Action
                      </Button>
                    )}
                  </CardContent>
                </Card>
                </Grid>
              ))
            )}
          </Grid>
        )}

        {activeTab === 4 && (
          <Box>
            {courses.length === 0 ? (
              <Card>
                <CardContent sx={{ textAlign: 'center', py: 6 }}>
                  <Typography color="text.secondary">No courses enrolled — session history will appear here once your child joins a course.</Typography>
                </CardContent>
              </Card>
            ) : (
              courses.map((course) => (
                <Accordion
                  key={course.id}
                  onChange={(_: React.SyntheticEvent, expanded: boolean) => {
                    if (expanded && !(course.id in sessionHistoryByCourse)) {
                      setSessionHistoryLoadingCourse(course.id);
                      getParentSessionHistory(course.id)
                        .then((items) => setSessionHistoryByCourse((prev) => ({ ...prev, [course.id]: items })))
                        .catch(() => setSessionHistoryByCourse((prev) => ({ ...prev, [course.id]: [] })))
                        .finally(() => setSessionHistoryLoadingCourse(null));
                    }
                  }}
                  sx={{ mb: 1 }}
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography fontWeight="medium">{course.name}</Typography>
                  </AccordionSummary>
                  <AccordionDetails sx={{ p: 0 }}>
                    {sessionHistoryLoadingCourse === course.id ? (
                      <Box display="flex" justifyContent="center" py={3}><CircularProgress size={24} /></Box>
                    ) : (
                      <SessionHistoryList sessions={sessionHistoryByCourse[course.id] ?? []} />
                    )}
                  </AccordionDetails>
                </Accordion>
              ))
            )}
          </Box>
        )}

        {activeTab === 5 && (
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h6">Parent Notes</Typography>
                <Button variant="contained" onClick={() => setShowNotesDialog(true)}>
                  Edit Notes
                </Button>
              </Box>
              <Paper sx={{ p: 3, bgcolor: 'grey.50' }}>
                <Typography variant="body1">
                  {child.parentNotes || 'No notes yet. Click "Edit Notes" to add your observations and reminders.'}
                </Typography>
              </Paper>
            </CardContent>
          </Card>
        )}
      </Container>

      {/* Message Dialog */}
      <Dialog open={showMessageDialog} onClose={() => setShowMessageDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Send Message to Teacher</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Subject"
            fullWidth
            variant="outlined"
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="Message"
            fullWidth
            multiline
            rows={4}
            variant="outlined"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowMessageDialog(false)}>Cancel</Button>
          <Button variant="contained">Send Message</Button>
        </DialogActions>
      </Dialog>

      {/* Notes Dialog */}
      <Dialog open={showNotesDialog} onClose={() => setShowNotesDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Parent Notes</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Your notes about your child's learning"
            fullWidth
            multiline
            rows={6}
            variant="outlined"
            value={parentNotes}
            onChange={(e) => setParentNotes(e.target.value)}
            placeholder="Add your observations, concerns, goals, or reminders here..."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowNotesDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveNotes}>Save Notes</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ParentChildDashboard;
